import React, { useState } from 'react';
import { Percent, Scale, X } from 'lucide-react';

const fmt = (n: number) => (Number.isFinite(n) ? n.toLocaleString('fa-IR') : '—');
const parseNum = (s: string) => parseFloat(String(s).replace(/[,٬\s]/g, '')) || 0;

const AmountCalculator: React.FC<{ open: boolean; onOpenChange: (v: boolean) => void }> = ({ open, onOpenChange }) => {
  const [mode, setMode] = useState<'item' | 'coef' | 'pct'>('item');
  const [qty, setQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [estimate, setEstimate] = useState('');
  const [coefficient, setCoefficient] = useState('');
  const [amount, setAmount] = useState('');
  const [pctBase, setPctBase] = useState('');
  const [pct, setPct] = useState('');

  if (!open) return null;

  const itemTotal = parseNum(qty) * parseNum(unitCost);
  const est = parseNum(estimate);
  const coef = parseNum(coefficient);
  const amt = parseNum(amount);
  const coefFromAmount = est > 0 && amt > 0 ? amt / est : 0;
  const amountFromCoef = est > 0 && coef > 0 ? est * coef : 0;
  const pctResult = (parseNum(pctBase) * parseNum(pct)) / 100;

  return (
    <div className="fixed z-[70] w-[340px] rounded-xl border border-emerald-200 bg-white shadow-2xl left-4 bottom-4 md:left-auto md:right-4" dir="rtl">
      <div className="flex items-center justify-between rounded-t-xl px-3 py-2 bg-[#0a4d29] text-white">
        <div className="flex items-center gap-2 text-sm font-bold">
          <Scale size={16} />
          ماشین حساب مبلغ / ضریب
        </div>
        <button type="button" onClick={() => onOpenChange(false)} className="p-1.5 rounded hover:bg-white/10">
          <X size={16} />
        </button>
      </div>
      <div className="flex border-b border-emerald-100 text-xs">
        {([
          ['item', 'مقدار × واحد'],
          ['coef', 'ضریب پیمانکار'],
          ['pct', 'درصد از مبلغ'],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setMode(k)}
            className={`flex-1 py-2 font-bold ${mode === k ? 'bg-emerald-50 text-emerald-900 border-b-2 border-emerald-600' : 'text-slate-600'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="p-3 space-y-3 text-sm">
        {mode === 'item' && (
          <>
            <div>
              <label className="text-xs text-slate-600 block mb-1">مقدار</label>
              <input value={qty} onChange={(e) => setQty(e.target.value)} className="w-full border rounded-lg px-3 py-2 font-mono" dir="ltr" />
            </div>
            <div>
              <label className="text-xs text-slate-600 block mb-1">بهای واحد</label>
              <input value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="w-full border rounded-lg px-3 py-2 font-mono" dir="ltr" />
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 font-mono font-bold text-emerald-900">
              بهای کل: {fmt(itemTotal)}
            </div>
          </>
        )}
        {mode === 'coef' && (
          <>
            <div>
              <label className="text-xs text-slate-600 block mb-1">برآورد اولیه</label>
              <input value={estimate} onChange={(e) => setEstimate(e.target.value)} className="w-full border rounded-lg px-3 py-2 font-mono" dir="ltr" />
            </div>
            <div>
              <label className="text-xs text-slate-600 block mb-1">ضریب پیمانکار</label>
              <input value={coefficient} onChange={(e) => setCoefficient(e.target.value)} className="w-full border rounded-lg px-3 py-2 font-mono" dir="ltr" placeholder="مثلا 1.0234" />
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-1">
              <p className="font-mono font-bold text-emerald-900">مبلغ = {fmt(amountFromCoef)}</p>
            </div>
            <div className="border-t pt-2">
              <label className="text-xs text-slate-600 block mb-1">یا: مبلغ نهایی → ضریب</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full border rounded-lg px-3 py-2 font-mono mb-2" dir="ltr" />
              <p className="font-mono text-slate-800">ضریب = {coefFromAmount > 0 ? coefFromAmount.toFixed(4) : '—'}</p>
            </div>
          </>
        )}
        {mode === 'pct' && (
          <>
            <div>
              <label className="text-xs text-slate-600 block mb-1">مبلغ پایه</label>
              <input value={pctBase} onChange={(e) => setPctBase(e.target.value)} className="w-full border rounded-lg px-3 py-2 font-mono" dir="ltr" />
            </div>
            <div>
              <label className="text-xs text-slate-600 block mb-1 flex items-center gap-1"><Percent size={12} /> درصد</label>
              <input value={pct} onChange={(e) => setPct(e.target.value)} className="w-full border rounded-lg px-3 py-2 font-mono" dir="ltr" />
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 font-mono font-bold text-amber-900">
              نتیجه: {fmt(pctResult)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AmountCalculator;
